import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { NAME_MAX, type SaleRole, type Seller, type SetupLink } from '@sellit/shared';
import { useMe } from '../admin/auth';
import { platform } from '../lib/api';
import { setAppTitle, timeAgo } from '../lib/format';
import { errorText, fieldErrors, SaleStatusChip, SetupLinkCard, useLoad, UserStatusChip } from './common';

export function SellerPage() {
  const { sellerId = '' } = useParams();
  const { data: seller, setData: setSeller, error } = useLoad(() => platform.seller(sellerId), [sellerId]);
  useEffect(() => setAppTitle(`${seller?.displayName ?? 'Seller'}`), [seller?.displayName]);

  if (error && !seller) return <main className="page narrow pa-page"><p className="error">{error}</p></main>;
  if (!seller) return <main className="page narrow pa-page muted">Loading…</main>;
  return <SellerDetail key={seller.id} seller={seller} onChange={setSeller} />;
}

function SellerDetail({ seller, onChange }: { seller: Seller; onChange: (s: Seller) => void }) {
  const me = useMe();
  const isSelf = me.user.id === seller.id;
  const [form, setForm] = useState({ displayName: seller.displayName, email: seller.email });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [link, setLink] = useState<SetupLink | null>(null);
  const dirty = form.displayName !== seller.displayName || form.email !== seller.email;

  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    setError(null);
    setFields({});
    setSaved(false);
    try {
      return await fn();
    } catch (err) {
      setError(errorText(err));
      setFields(fieldErrors(err));
      return undefined;
    } finally {
      setBusy(false);
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    const updated = await run(() => platform.updateSeller(seller.id, form));
    if (updated) {
      onChange(updated);
      setSaved(true);
    }
  }

  async function toggleStatus() {
    const disable = seller.status === 'ACTIVE';
    if (disable && !window.confirm(`Disable ${seller.displayName}? They'll be logged out and can't log in until you reactivate them. Their sales stay as they are.`)) return;
    const updated = await run(() => platform.updateSeller(seller.id, { status: disable ? 'DISABLED' : 'ACTIVE' }));
    if (updated) onChange(updated);
  }

  async function newLink() {
    const issued = await run(() => platform.setupLink(seller.id, false));
    if (issued) {
      setLink(issued);
      onChange({ ...seller });
    }
  }

  return (
    <main className="page narrow pa-page">
      <Link className="link small" to="/platform-admin/sellers">← Sellers</Link>
      <header className="pa-head">
        <h1>{seller.displayName}</h1>
        <UserStatusChip status={seller.status} hasPassword={seller.hasPassword} />
      </header>
      <p className="muted small">
        {seller.role === 'PLATFORM_ADMIN' ? 'Platform admin · ' : ''}
        {seller.lastLoginAt ? `Last login ${timeAgo(seller.lastLoginAt)}` : seller.hasPassword ? 'Never logged in' : "Hasn't set a password yet"}
      </p>

      <form className="stack card" onSubmit={save}>
        <label className="field">
          <span>Name</span>
          <input value={form.displayName} maxLength={NAME_MAX} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          {fields.displayName && <small className="error">{fields.displayName}</small>}
        </label>
        <label className="field">
          <span>Email (their login)</span>
          <input type="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          {fields.email && <small className="error">{fields.email}</small>}
        </label>
        <button className="btn primary" disabled={busy || !dirty}>{saved && !dirty ? 'Saved' : 'Save changes'}</button>
      </form>

      {error && <p className="error" role="alert">{error}</p>}

      <section className="pa-section">
        <h2>Sales</h2>
        {seller.sales.length === 0 ? (
          <p className="muted">Not assigned to any sale yet.</p>
        ) : (
          <ul className="pa-list">
            {seller.sales.map((s) => (
              <li key={s.id}>
                <Link className="pa-row" to={`/platform-admin/sales/${s.id}`}>
                  <span className="pa-row-main">
                    <strong>{s.title}</strong>
                    <span className="muted small">/{s.slug} · {s.role === 'OWNER' ? 'Owner' : 'Editor'}</span>
                  </span>
                  <SaleStatusChip status={s.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
        <AssignToSale seller={seller} onAssigned={onChange} />
        <Link className="btn" to={`/platform-admin/sales/new?seller=${seller.id}`}>Create a new sale for them</Link>
      </section>

      <section className="pa-section">
        <h2>Access</h2>
        {link && <SetupLinkCard link={link} name={seller.displayName} />}
        <div className="actions">
          {seller.status === 'ACTIVE' && (
            <button className="btn" onClick={newLink} disabled={busy}>
              {seller.hasPassword ? 'Password reset link' : 'New setup link'}
            </button>
          )}
          {!isSelf && (
            <button className={`btn ${seller.status === 'ACTIVE' ? 'danger' : ''}`} onClick={toggleStatus} disabled={busy}>
              {seller.status === 'ACTIVE' ? 'Disable access' : 'Reactivate'}
            </button>
          )}
        </div>
        <p className="muted small">
          {seller.status === 'DISABLED'
            ? "Disabled: they can't log in. Their sales and inquiries are kept."
            : 'A new link replaces any earlier one. A password reset logs them out everywhere once they use it.'}
        </p>
      </section>
    </main>
  );
}

/** Adds this seller to an existing sale, keeping that sale's other sellers. */
function AssignToSale({ seller, onAssigned }: { seller: Seller; onAssigned: (s: Seller) => void }) {
  const { data } = useLoad(() => platform.sales());
  const [saleId, setSaleId] = useState('');
  const [role, setRole] = useState<SaleRole>('OWNER');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = data?.sales.filter((s) => !seller.sales.some((own) => own.id === s.id)) ?? [];
  if (!options.length) return null;

  async function assign() {
    const sale = options.find((s) => s.id === saleId);
    if (!sale) return;
    setBusy(true);
    setError(null);
    try {
      await platform.setMembers(sale.id, [...sale.members.map((m) => ({ userId: m.userId, role: m.role })), { userId: seller.id, role }]);
      onAssigned(await platform.seller(seller.id));
      setSaleId('');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="assign card">
      <strong>Add to an existing sale</strong>
      <div className="assign-row">
        <select value={saleId} onChange={(e) => setSaleId(e.target.value)} aria-label="Sale">
          <option value="">Choose a sale…</option>
          {options.map((s) => <option key={s.id} value={s.id}>{s.title} (/{s.slug})</option>)}
        </select>
        <select value={role} onChange={(e) => setRole(e.target.value as SaleRole)} aria-label="Role">
          <option value="OWNER">Owner</option>
          <option value="EDITOR">Editor</option>
        </select>
        <button className="btn" onClick={assign} disabled={!saleId || busy}>Add</button>
      </div>
      <p className="muted small">Owners can change sale settings and get inquiry emails. Editors manage items and inquiries.</p>
      {error && <p className="error small">{error}</p>}
    </div>
  );
}
